import { useCallback } from "react";
import { useSelector } from "react-redux";
import {
  useAddDoctorFavouriteMutation,
  useGetFavouriteDoctorsListIdsQuery,
  useRemoveDoctorFavouriteMutation,
} from "@/Redux/apis/doctor";
import { toast } from "react-toastify";
import { doctorsApi } from "@/Redux/apis/doctor";
import { useDispatch } from "react-redux";

/**
 * Hook for managing rep favourites (doctor → rep relationship).
 * Uses RTK Query optimistic updates with automatic rollback on error.
 * No local state mirroring, no manual refetch, no redundancy.
 */
export function useRepFavourite() {
  const dispatch = useDispatch();
  const users = useSelector((state) => state?.userData?.userData?.user);
  const userId = users?._id;

  const favouriteIdsQuery = useGetFavouriteDoctorsListIdsQuery(
    { id: userId },
    { skip: !userId }
  );

  const [addFavouriteApi] = useAddDoctorFavouriteMutation();
  const [removeFavouriteApi] = useRemoveDoctorFavouriteMutation();

  const favourites = favouriteIdsQuery?.data?.data ?? [];
  const isLoading = favouriteIdsQuery.isLoading;

  const isFavourite = useCallback(
    (repId) => favourites.some((fav) => fav?.repId === repId && fav?.userId === userId),
    [favourites, userId]
  );

  const addFavourite = useCallback(
    async (rep) => {
      const payload = {
        userId,
        repId: rep?._id,
        type: "reps",
        name: `${rep?.firstName} ${rep?.lastName}`,
      };

      const patchResult = dispatch(
        doctorsApi.util.updateQueryData("getFavouriteDoctorsListIds", { id: userId }, (draft) => {
          draft.data = [...(draft.data || []), payload];
        })
      );

      try {
        await addFavouriteApi(payload).unwrap();
      } catch (err) {
        patchResult.undo();
        toast.error(err?.data?.message ?? "Failed to add favourite");
      }
    },
    [userId, addFavouriteApi, dispatch]
  );

  const removeFavourite = useCallback(
    async (repId) => {
      const payload = { userId, repId, type: "reps" };

      const patchResult = dispatch(
        doctorsApi.util.updateQueryData("getFavouriteDoctorsListIds", { id: userId }, (draft) => {
          draft.data = (draft.data || []).filter(
            (fav) => !(fav?.userId === userId && fav?.repId === repId)
          );
        })
      );

      try {
        await removeFavouriteApi(payload).unwrap();
      } catch (err) {
        patchResult.undo();
        toast.error(err?.data?.message ?? "Failed to remove favourite");
      }
    },
    [userId, removeFavouriteApi, dispatch]
  );

  const toggleFavourite = useCallback(
    async (e, rep) => {
      e.preventDefault();
      if (isFavourite(rep?._id)) {
        await removeFavourite(rep?._id);
      } else {
        await addFavourite(rep);
      }
    },
    [isFavourite, addFavourite, removeFavourite]
  );

  return { favourites, isLoading, isFavourite, addFavourite, removeFavourite, toggleFavourite };
}

/**
 * Hook for managing pipeline-drug "I'm Interested" toggles.
 *
 * Persistence reuses the existing `doctorFavouriteModel` collection
 * (`/doctors/add-doctor-favourite` and `/doctors/remove-doctor-favourite`)
 * with `type: "pipeline"` as a third discriminator alongside the existing
 * `"product"` and `"reps"` values. No new collection or endpoint — the
 * pipeline product's _id is stored in the same `productId` slot, and the
 * Favorites page can branch on `type` to render a pipeline card.
 *
 * Mirrors useProductFavourite's optimistic-update pattern.
 */
export function usePipelineInterest() {
  const dispatch = useDispatch();
  const users = useSelector((state) => state?.userData?.userData?.user);
  const userId = users?._id;

  const favouriteIdsQuery = useGetFavouriteDoctorsListIdsQuery(
    { id: userId },
    { skip: !userId }
  );

  const [addFavouriteApi] = useAddDoctorFavouriteMutation();
  const [removeFavouriteApi] = useRemoveDoctorFavouriteMutation();

  const favourites = favouriteIdsQuery?.data?.data ?? [];
  const isLoading = favouriteIdsQuery.isLoading;

  const isInterested = useCallback(
    (pipelineId) =>
      favourites.some(
        (fav) =>
          fav?.type === "pipeline" &&
          fav?.userId === userId &&
          fav?.productId === pipelineId
      ),
    [favourites, userId]
  );

  const addInterest = useCallback(
    async (drug) => {
      const pipelineId = drug?._id ?? drug?.id;
      if (!pipelineId) return;

      const payload = {
        userId,
        productId: pipelineId,
        type: "pipeline",
        image: drug?.image ?? null,
        name: drug?.displayName ?? drug?.name ?? "Pipeline drug",
        genericName: drug?.slug ?? drug?.genericName ?? "",
      };

      const patchResult = dispatch(
        doctorsApi.util.updateQueryData("getFavouriteDoctorsListIds", { id: userId }, (draft) => {
          draft.data = [...(draft.data || []), payload];
        })
      );

      try {
        await addFavouriteApi(payload).unwrap();
      } catch (err) {
        patchResult.undo();
        toast.error(err?.data?.message ?? "Failed to mark as interested");
      }
    },
    [userId, addFavouriteApi, dispatch]
  );

  const removeInterest = useCallback(
    async (pipelineId) => {
      const payload = { userId, productId: pipelineId, type: "pipeline" };

      const patchResult = dispatch(
        doctorsApi.util.updateQueryData("getFavouriteDoctorsListIds", { id: userId }, (draft) => {
          draft.data = (draft.data || []).filter(
            (fav) =>
              !(
                fav?.type === "pipeline" &&
                fav?.userId === userId &&
                fav?.productId === pipelineId
              )
          );
        })
      );

      try {
        await removeFavouriteApi(payload).unwrap();
      } catch (err) {
        patchResult.undo();
        toast.error(err?.data?.message ?? "Failed to remove interest");
      }
    },
    [userId, removeFavouriteApi, dispatch]
  );

  const toggleInterest = useCallback(
    async (e, drug) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      const pipelineId = drug?._id ?? drug?.id;
      if (!pipelineId) return;
      if (isInterested(pipelineId)) {
        await removeInterest(pipelineId);
      } else {
        await addInterest(drug);
      }
    },
    [isInterested, addInterest, removeInterest]
  );

  return {
    favourites,
    isLoading,
    isInterested,
    addInterest,
    removeInterest,
    toggleInterest,
  };
}

/**
 * Hook for managing product favourites (doctor → product relationship).
 * Uses RTK Query optimistic updates with automatic rollback on error.
 */
export function useProductFavourite() {
  const dispatch = useDispatch();
  const users = useSelector((state) => state?.userData?.userData?.user);
  const userId = users?._id;

  const favouriteIdsQuery = useGetFavouriteDoctorsListIdsQuery(
    { id: userId },
    { skip: !userId }
  );

  const [addFavouriteApi] = useAddDoctorFavouriteMutation();
  const [removeFavouriteApi] = useRemoveDoctorFavouriteMutation();

  const favourites = favouriteIdsQuery?.data?.data ?? [];
  const isLoading = favouriteIdsQuery.isLoading;

  const favouriteProductIds = useCallback(
    (productId, familyIds = []) => {
      const ids = new Set([productId, ...(Array.isArray(familyIds) ? familyIds : [])]
        .filter(Boolean).map(String));
      return [...new Set(favourites
        .filter((fav) => fav?.userId === userId && fav?.productId && ids.has(String(fav.productId)))
        .map((fav) => fav.productId))];
    },
    [favourites, userId]
  );
  const isFavourite = useCallback(
    (productId, familyIds) => favouriteProductIds(productId, familyIds).length > 0,
    [favouriteProductIds]
  );

  const addFavourite = useCallback(
    async (product) => {
      const payload = {
        userId,
        productId: product?._id ?? product?.id,
        type: "product",
        image: product?.image,
        name: product?.openfda?.brand_name,
        genericName: product?.openfda?.generic_name,
      };

      const patchResult = dispatch(
        doctorsApi.util.updateQueryData("getFavouriteDoctorsListIds", { id: userId }, (draft) => {
          draft.data = [...(draft.data || []), payload];
        })
      );

      try {
        await addFavouriteApi(payload).unwrap();
      } catch (err) {
        patchResult.undo();
        toast.error(err?.data?.message ?? "Failed to add favourite");
      }
    },
    [userId, addFavouriteApi, dispatch]
  );

  const removeFavourite = useCallback(
    async (productId) => {
      const payload = { userId, productId, type: "product" };

      const patchResult = dispatch(
        doctorsApi.util.updateQueryData("getFavouriteDoctorsListIds", { id: userId }, (draft) => {
          draft.data = (draft.data || []).filter(
            (fav) => !(fav?.userId === userId && fav?.productId === productId)
          );
        })
      );

      try {
        await removeFavouriteApi(payload).unwrap();
      } catch (err) {
        patchResult.undo();
        toast.error(err?.data?.message ?? "Failed to remove favourite");
      }
    },
    [userId, removeFavouriteApi, dispatch]
  );

  const toggleFavourite = useCallback(
    async (e, product) => {
      e.preventDefault();
      const productId = product?._id ?? product?.id;
      const selectedIds = favouriteProductIds(productId, product?.productFamilyIds);
      if (selectedIds.length) {
        // Source IDs stay intact. Unfavorite the actual saved copies; adding
        // always saves only the currently displayed representative below.
        for (const selectedId of selectedIds) await removeFavourite(selectedId);
      } else {
        await addFavourite(product);
      }
    },
    [favouriteProductIds, addFavourite, removeFavourite]
  );

  return { favourites, isLoading, isFavourite, addFavourite, removeFavourite, toggleFavourite };
}
